import { ReactNode, useMemo, useState } from "react";
import { AlertCircle, FolderPlus, Plus, Search } from "lucide-react";
import { Button } from "~/components/ui/button";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useFetcher } from "~/hooks/useFetcher";
import { useViewMode } from "~/hooks/useViewMode";
import { cn } from "~/lib/utils";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { ListLoadingSkeleton } from "~/pages/manifold-nextgen/components/ListLoadingSkeleton";
import { ViewToggle } from "~/pages/manifold-nextgen/components/ViewToggle";
import { ProjectList as ProjectListType, projectListPath } from "~/services/projects";
import { NewProjectDialog } from "./NewProjectDialog";
import { ProjectCard } from "./ProjectCard";

export const Projects = () => {
  const { data, error, isLoading } = useFetcher<ProjectListType>(projectListPath());
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("nextgen-view-mode");

  const headerActions = useMemo(
    () =>
      [
        <Button key="new-project" size="sm" className="text-xs" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" />
          New Project
        </Button>,
      ] as [ReactNode],
    []
  );

  useNextGenHeader({ title: "Projects", actions: headerActions });

  const filtered = (() => {
    if (!data?.items) {
      return [];
    }
    if (!search.trim()) {
      return data.items;
    }
    const lower = search.toLowerCase();
    return data.items.filter((p) => p.name.toLowerCase().includes(lower));
  })();

  return (
    <>
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-lg">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search projects"
              className={cn(
                "h-8 w-full rounded-md border border-input bg-transparent pl-9 pr-3 py-1",
                "text-sm placeholder:text-muted-foreground shadow-xs",
                "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                "transition-[color,box-shadow]"
              )}
            />
          </div>
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>

        {isLoading && viewMode === "list" && (
          <ListLoadingSkeleton
            aria-label="Loading projects"
            columns={[{ width: "w-36", height: "h-6", rounded: true }, { width: "w-36" }]}
          />
        )}
        {isLoading && viewMode === "card" && (
          <div
            role="status"
            aria-label="Loading projects"
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-xl border border-border bg-muted"
              />
            ))}
          </div>
        )}

        {!isLoading && error && (
          <div
            role="alert"
            className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
          >
            <AlertCircle className="size-8" aria-hidden="true" />
            <p className="text-sm font-medium">Failed to load projects</p>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <FolderPlus className="size-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium text-foreground">
              {search ? "No projects match your search" : "No projects yet"}
            </p>
            {!search && (
              <p className="text-xs text-muted-foreground">Create one to group your work.</p>
            )}
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && viewMode === "card" && (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
            {filtered.map((project) => (
              <li key={project.id} className="flex flex-col">
                <ProjectCard project={project} view="card" />
              </li>
            ))}
          </ul>
        )}

        {!isLoading && !error && filtered.length > 0 && viewMode === "list" && (
          <DividedList
            items={filtered}
            renderItem={(project) => <ProjectCard project={project} view="list" />}
          />
        )}
      </div>

      <NewProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
};
